// "Data" of a HOSTED project: the tables of its database as the hosted design version has them
// (name, comment, row count). GET /api/projects/{id}/data/tables; every member who sees the
// project reads (operate/browse.py). A table opens in DataTablePage. The spreadsheet template of
// every table downloads here (TemplateDownloads). Above the tables, imports waiting for a data
// steward's review (ImportReviewPanel; shown only when there are some).
import { Link, useParams } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import { Empty, ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Card } from "../../components/Shared";
import { useApi } from "../../useApi";
import TemplateDownloads from "./TemplateDownloads";
import ImportReviewPanel from "./ImportReviewPanel";

const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)",
                 whiteSpace: "nowrap" };
const cell = { padding: "10px 16px", borderTop: "1px solid #e5e7eb", fontSize: 14, verticalAlign: "top" };
const head = { padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#4b5563",
               textTransform: "uppercase", letterSpacing: "0.05em" };

export default function DataTablesPage() {
  const { projectId } = useParams();
  const project = useApi(`/projects/${projectId}`);
  const tables = useApi(`/projects/${projectId}/data/tables`);
  const access = useApi(`/projects/${projectId}/data-access`);
  const p = project.data;
  const name = p ? p.name : "…";

  return (
    <AppLayout title={`Data — ${name}`}
               crumbs={[{ label: "Projects", to: "/projects" }, { label: name, to: `/projects/${projectId}` },
                        { label: "Data" }]}>
      <ErrorNote error={project.error || tables.error} />
      <ImportReviewPanel projectId={projectId} canReview={!!(access.data && access.data.can_review_imports)} />
      {tables.loading && !tables.data && <Loading />}
      {tables.data && tables.data.length === 0 && <Empty>The hosted design has no tables.</Empty>}
      {tables.data && tables.data.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <TemplateDownloads projectId={projectId} stem={p ? `${p.slug}_all_tables` : "tables"} />
        </div>
      )}
      {tables.data && tables.data.length > 0 && (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <caption style={srOnly}>Tables of the project database</caption>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th scope="col" style={head}>Table</th>
                  <th scope="col" style={head}>Description</th>
                  <th scope="col" style={{ ...head, textAlign: "right" }}>Rows</th>
                  <th scope="col" style={{ ...head, textAlign: "right" }}>Columns</th>
                </tr>
              </thead>
              <tbody>
                {tables.data.map(t => (
                  <tr key={t.name}>
                    <td data-label="Table" style={cell}>
                      <Link to={`/projects/${projectId}/data/${encodeURIComponent(t.name)}`}
                            style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "var(--dp-accent-dark)" }}>
                        {t.name}
                      </Link>
                      {t.kind === "hub" && <span style={{ marginLeft: 8 }}><Badge color="blue">shared entity</Badge></span>}
                    </td>
                    <td data-label="Description" style={{ ...cell, color: "#374151" }}>{t.comment || "—"}</td>
                    <td data-label="Rows" style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {t.row_count == null ? "—" : t.row_count.toLocaleString("en")}
                    </td>
                    <td data-label="Columns" style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{t.column_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </AppLayout>
  );
}
